// ── Expert configuration for the council / debate mode ──────────────────────

export interface ExpertOption {
  value: string;
  label: string;
  category: ExpertCategory;
  /** One-line elevator pitch shown in the picker tooltip / subtitle */
  description: string;
}

/** Expert categories for grouped display */
export type ExpertCategory =
  | "Analytical & Reasoning"
  | "Health & Wellness"
  | "Society & Law"
  | "Business & Finance"
  | "Life & Personal"
  | "Technology"
  | "Environment & Science"
  | "Creative";

/** Display order and emoji for each category */
export const categoryMeta: Record<
  ExpertCategory,
  { emoji: string; order: number }
> = {
  "Analytical & Reasoning": { emoji: "🧠", order: 0 },
  "Health & Wellness": { emoji: "🏥", order: 1 },
  "Society & Law": { emoji: "⚖️", order: 2 },
  "Business & Finance": { emoji: "💰", order: 3 },
  "Life & Personal": { emoji: "👨‍👩‍👧", order: 4 },
  Technology: { emoji: "💻", order: 5 },
  "Environment & Science": { emoji: "🌍", order: 6 },
  Creative: { emoji: "🎨", order: 7 },
};

/** Maximum number of experts allowed in a single debate */
export const MAX_EXPERTS = 5;

/** All available expert specialisations, organised by category */
export const expertOptions: ExpertOption[] = [
  // ── 🧠 Analytical & Reasoning ────────────────────────────────────────────
  {
    value: "logic",
    label: "Logician",
    category: "Analytical & Reasoning",
    description: "Forensic analyst — premises, fallacies, hidden assumptions",
  },
  {
    value: "statistician",
    label: "Statistician",
    category: "Analytical & Reasoning",
    description: "Study design, effect sizes, correlation vs causation",
  },
  {
    value: "decision",
    label: "Decision Scientist",
    category: "Analytical & Reasoning",
    description: "Maps tradeoffs, probabilities, and expected outcomes",
  },
  {
    value: "research",
    label: "Research Librarian",
    category: "Analytical & Reasoning",
    description: "Investigative librarian — evidence, sources, consensus",
  },
  {
    value: "philosopher",
    label: "Philosopher",
    category: "Analytical & Reasoning",
    description:
      "Deep conceptual analysis, thought experiments, first principles",
  },
  {
    value: "systems",
    label: "Systems Thinker",
    category: "Analytical & Reasoning",
    description: "Feedback loops, emergent behaviour, second-order effects",
  },
  {
    value: "devils_advocate",
    label: "Devil's Advocate",
    category: "Analytical & Reasoning",
    description: "Steelmans the opposing view and stress-tests your position",
  },
  {
    value: "economist",
    label: "Economist",
    category: "Analytical & Reasoning",
    description: "Incentives, market dynamics, macro & micro economic analysis",
  },

  // ── 🏥 Health & Wellness ──────────────────────────────────────────────────
  {
    value: "medical",
    label: "Physician",
    category: "Health & Wellness",
    description: "Physician — guidelines, physiology, evidence-based medicine",
  },
  {
    value: "nutrition",
    label: "Dietician",
    category: "Health & Wellness",
    description: "Registered dietician — evidence-backed meal guidance",
  },
  {
    value: "fitness",
    label: "Strength Coach",
    category: "Health & Wellness",
    description: "Strength coach — safe, scalable, evidence-based training",
  },
  {
    value: "psychology",
    label: "Psychologist",
    category: "Health & Wellness",
    description: "Clinical psychologist — cognitive patterns, interventions",
  },
  {
    value: "therapist",
    label: "Therapist",
    category: "Health & Wellness",
    description: "Licensed therapist — anxiety, depression, coping strategies",
  },
  {
    value: "sleep",
    label: "Sleep Scientist",
    category: "Health & Wellness",
    description: "Circadian rhythms, sleep hygiene, and performance impact",
  },
  {
    value: "neuroscientist",
    label: "Neuroscientist",
    category: "Health & Wellness",
    description: "Brain function, neuroplasticity, cognitive performance",
  },
  {
    value: "pharmacologist",
    label: "Pharmacologist",
    category: "Health & Wellness",
    description:
      "Drug interactions, mechanisms of action, risk-benefit analysis",
  },

  // ── ⚖️ Society & Law ─────────────────────────────────────────────────────
  {
    value: "legal",
    label: "Lawyer",
    category: "Society & Law",
    description: "Legal analyst — statutes, liabilities, jurisdictional nuance",
  },
  {
    value: "ethics",
    label: "Ethicist",
    category: "Society & Law",
    description: "Ethicist — competing values, hidden harms, fair tradeoffs",
  },
  {
    value: "history",
    label: "Historian",
    category: "Society & Law",
    description: "Historian — patterns, parallels, and contextual analysis",
  },
  {
    value: "religion",
    label: "Religion Scholar",
    category: "Society & Law",
    description: "Comparative religion scholar — texts, traditions, interfaith",
  },
  {
    value: "political",
    label: "Political Scientist",
    category: "Society & Law",
    description: "Policy, governance, ideology, and geopolitical dynamics",
  },
  {
    value: "sociologist",
    label: "Sociologist",
    category: "Society & Law",
    description: "Social structures, inequality, group dynamics, institutions",
  },
  {
    value: "anthropologist",
    label: "Anthropologist",
    category: "Society & Law",
    description: "Cultural context, human behaviour, cross-cultural comparison",
  },
  {
    value: "urban_planner",
    label: "Urban Planner",
    category: "Society & Law",
    description: "City design, infrastructure, housing, transit, zoning",
  },
  {
    value: "policy",
    label: "Policy Analyst",
    category: "Society & Law",
    description:
      "Regulatory impact, implementation feasibility, public interest",
  },

  // ── 💰 Business & Finance ────────────────────────────────────────────────
  {
    value: "finance",
    label: "Financial Planner",
    category: "Business & Finance",
    description: "CFP & behavioral economist — costs, biases, compounding",
  },
  {
    value: "startup",
    label: "Startup Founder",
    category: "Business & Finance",
    description: "Lean strategy, product-market fit, growth, and execution",
  },
  {
    value: "marketing",
    label: "Marketing Strategist",
    category: "Business & Finance",
    description: "Brand positioning, growth channels, consumer psychology",
  },
  {
    value: "product",
    label: "Product Manager",
    category: "Business & Finance",
    description: "Roadmap prioritisation, user needs, shipping tradeoffs",
  },
  {
    value: "supply_chain",
    label: "Supply Chain Expert",
    category: "Business & Finance",
    description: "Logistics, procurement, resilience, and cost optimisation",
  },
  {
    value: "hr",
    label: "HR / Org Psychologist",
    category: "Business & Finance",
    description: "Team dynamics, hiring, culture, organisational design",
  },

  // ── 👨‍👩‍👧 Life & Personal ──────────────────────────────────────────────────
  {
    value: "parenting",
    label: "Child Development Specialist",
    category: "Life & Personal",
    description: "Child development specialist — ages, stages, family dynamics",
  },
  {
    value: "relationships",
    label: "Relationship Counselor",
    category: "Life & Personal",
    description:
      "Couples therapist — communication, attachment, conflict resolution",
  },
  {
    value: "career",
    label: "Career Coach",
    category: "Life & Personal",
    description: "Career pivots, negotiation, personal branding, job strategy",
  },
  {
    value: "life_coach",
    label: "Life Coach",
    category: "Life & Personal",
    description: "Goal setting, accountability, habit design, life transitions",
  },
  {
    value: "educator",
    label: "Educator",
    category: "Life & Personal",
    description:
      "Pedagogy, learning science, curriculum design, skill building",
  },

  // ── 💻 Technology ─────────────────────────────────────────────────────────
  {
    value: "coding",
    label: "Software Engineer",
    category: "Technology",
    description: "Senior engineer — clean code, edge cases, runnable examples",
  },
  {
    value: "ai_researcher",
    label: "AI Researcher",
    category: "Technology",
    description:
      "Model architectures, training dynamics, AI safety, capabilities",
  },
  {
    value: "cybersecurity",
    label: "Security Expert",
    category: "Technology",
    description:
      "Threat modeling, privacy, vulnerabilities, and risk mitigation",
  },
  {
    value: "data_engineer",
    label: "Data Engineer",
    category: "Technology",
    description: "Pipelines, data quality, architecture, and scalability",
  },
  {
    value: "ux_designer",
    label: "UX Designer",
    category: "Technology",
    description: "User research, interaction design, accessibility, usability",
  },

  // ── 🌍 Environment & Science ──────────────────────────────────────────────
  {
    value: "climate",
    label: "Climate Scientist",
    category: "Environment & Science",
    description: "Climate models, emissions, mitigation, and adaptation",
  },
  {
    value: "ecologist",
    label: "Ecologist",
    category: "Environment & Science",
    description: "Ecosystems, biodiversity, conservation, sustainability",
  },
  {
    value: "physicist",
    label: "Physicist",
    category: "Environment & Science",
    description: "Fundamental forces, energy, quantum mechanics, cosmology",
  },
  {
    value: "chemist",
    label: "Chemist",
    category: "Environment & Science",
    description: "Molecular behaviour, materials, reactions, safety",
  },
  {
    value: "biologist",
    label: "Biologist",
    category: "Environment & Science",
    description: "Genetics, evolution, cell biology, and life systems",
  },

  // ── 🎨 Creative ──────────────────────────────────────────────────────────
  {
    value: "creative",
    label: "Creative Director",
    category: "Creative",
    description: "Stories, bold prototypes, lateral thinking, reframing",
  },
  {
    value: "storyteller",
    label: "Storyteller",
    category: "Creative",
    description: "Narrative design, world-building, story structure, voice",
  },
  {
    value: "architect",
    label: "Architect",
    category: "Creative",
    description:
      "Spatial design, form, function, sustainability in built environments",
  },
  {
    value: "game_designer",
    label: "Game Designer",
    category: "Creative",
    description: "Game mechanics, player psychology, engagement loops, balance",
  },
  {
    value: "filmmaker",
    label: "Filmmaker",
    category: "Creative",
    description:
      "Visual storytelling, cinematography, editing, audience impact",
  },
];

/** Helper: get all unique categories in display order */
export function getOrderedCategories(): ExpertCategory[] {
  const seen = new Set<ExpertCategory>();
  const cats: ExpertCategory[] = [];
  for (const opt of expertOptions) {
    if (!seen.has(opt.category)) {
      seen.add(opt.category);
      cats.push(opt.category);
    }
  }
  return cats.sort(
    (a, b) => (categoryMeta[a]?.order ?? 99) - (categoryMeta[b]?.order ?? 99),
  );
}

/** Helper: group expert options by category */
export function getExpertsByCategory(): Record<ExpertCategory, ExpertOption[]> {
  const grouped = {} as Record<ExpertCategory, ExpertOption[]>;
  for (const opt of expertOptions) {
    if (!grouped[opt.category]) grouped[opt.category] = [];
    grouped[opt.category].push(opt);
  }
  return grouped;
}
